import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '@/components/ui/tooltip';
import { useAuth } from '@/contexts/AuthContext';
import {
  LayoutDashboard, Users, Building2, FileText, ShieldCheck,
  Mail, ListTodo, BarChart3, Settings, Upload, ChevronLeft,
  ChevronRight, HelpCircle, BookOpen, Layers, User
} from 'lucide-react';
import { BrandLogo } from '@/components/BrandLogo';

const navItems = [
  { section: 'Overview' },
  { label: 'Dashboard', path: '/', icon: LayoutDashboard },
  { section: 'Management' },
  { label: 'Vendors', path: '/vendors', icon: Users },
  { label: 'Properties', path: '/properties', icon: Building2, adminOnly: true },
  { label: 'Documents', path: '/documents', icon: FileText },
  { section: 'Compliance' },
  { label: 'Rules', path: '/compliance', icon: ShieldCheck },
  { label: 'Requirements', path: '/compliance/settings', icon: Layers, adminOnly: true },
  { label: 'Communications', path: '/communications', icon: Mail },
  { label: 'Tasks', path: '/tasks', icon: ListTodo },
  { section: 'Insights' },
  { label: 'Reports', path: '/reports', icon: BarChart3, adminOnly: true },
  { section: 'Admin', adminOnly: true },
  { label: 'Bulk Import', path: '/bulk-import', icon: Upload, adminOnly: true },
  { label: 'Settings', path: '/settings', icon: Settings, adminOnly: true },
  { section: 'Support' },
  { label: 'Knowledge Base', path: '/guide', icon: BookOpen },
  { label: 'Help & Support', path: '/support', icon: HelpCircle },
  { label: 'Profile', path: '/profile', icon: User },
];

export const Sidebar = ({ mobileOpen = false, onMobileClose }) => {
  const [collapsed, setCollapsed] = useState(false);
  const location = useLocation();
  const { isAdmin } = useAuth();

  return (
    <TooltipProvider delayDuration={0}>
      <aside
        data-testid="app-sidebar"
        className={cn(
          'fixed left-0 top-0 z-50 h-screen border-r border-border bg-card flex flex-col transition-transform duration-200',
          'lg:translate-x-0 lg:transition-[width] lg:duration-200',
          collapsed ? 'lg:w-[76px]' : 'lg:w-[260px]',
          'w-[280px]',
          mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        )}
      >
        {/* Logo */}
        <div className="flex h-16 items-center gap-3 px-4 border-b border-border">
          {collapsed ? (
            <BrandLogo width={55} />
          ) : (
            <BrandLogo width={170} />
          )}
        </div>

        {/* Nav */}
        <ScrollArea className="flex-1 px-3 py-3">
          <nav className="flex flex-col gap-0.5">
            {navItems.map((item, i) => {
              // RBAC: Hide admin-only items for non-admins
              if (item.adminOnly && !isAdmin) return null;

              if (item.section) {
                if (collapsed) return <Separator key={i} className="my-2" />;
                return (
                  <span key={i} className="px-3 pt-5 pb-2 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                    {item.section}
                  </span>
                );
              }

              const Icon = item.icon;
              const isActive = location.pathname === item.path ||
                (item.path !== '/' && location.pathname.startsWith(item.path));

              const link = (
                <Link
                  key={item.path}
                  to={item.path}
                  data-testid={`sidebar-nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                  onClick={() => onMobileClose?.()}
                  className={cn(
                    'flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors duration-150',
                    isActive
                      ? 'bg-muted text-foreground font-medium'
                      : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground',
                    collapsed && 'justify-center px-0'
                  )}
                >
                  <Icon className="h-4.5 w-4.5 shrink-0" style={{ width: 18, height: 18 }} />
                  {!collapsed && <span>{item.label}</span>}
                </Link>
              );

              if (collapsed) {
                return (
                  <Tooltip key={item.path}>
                    <TooltipTrigger asChild>{link}</TooltipTrigger>
                    <TooltipContent side="right">{item.label}</TooltipContent>
                  </Tooltip>
                );
              }
              return link;
            })}
          </nav>
        </ScrollArea>

        {/* Collapse toggle */}
        <div className="border-t border-border p-3">
          <Button
            variant="ghost"
            size="sm"
            data-testid="sidebar-collapse-button"
            onClick={() => setCollapsed(c => !c)}
            className={cn('w-full', collapsed ? 'justify-center' : 'justify-start gap-2')}
          >
            {collapsed ? <ChevronRight className="h-4 w-4" /> : <><ChevronLeft className="h-4 w-4" /><span>Collapse</span></>}
          </Button>
        </div>
      </aside>
    </TooltipProvider>
  );
};
